import React, { useState, useMemo } from 'react';
import { Plus, Sparkles, Info } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { FORMULA_PRESETS, getFieldRefs } from '../../lib/formCalc';

/**
 * FormulaHelper — compact row of tools that sits below a formula input.
 *
 * Features:
 *   1. Field-reference picker: lists all fields in the current form, grouped by
 *      type. Clicking a row inserts the exact {field_id} or
 *      {tableId.col.colKey.agg} token into the formula at the caret position.
 *   2. Preset library: pre-canned healthcare + billing formulas (BMI, BSA,
 *      MAP, Age-from-DOB, Days-between, Line total, Cross-table sum, etc.).
 *      Clicking a preset replaces the formula (with a confirm prompt if the
 *      formula is non-empty).
 *   3. Syntax cheatsheet tooltip.
 *
 * Props:
 *   - formula: current formula string
 *   - onChange: (newFormula) => void
 *   - allFields: full `fields` array from the form builder state
 *   - currentFieldId: the field being edited (filtered out of the picker)
 *   - context: 'field' | 'row' — 'row' adds the row.colKey suggestions
 *   - testIdPrefix: prefix for data-testid attributes
 */
export default function FormulaHelper({
  formula = '',
  onChange,
  allFields = [],
  currentFieldId = null,
  context = 'field',
  testIdPrefix = 'formula',
}) {
  const [refSearch, setRefSearch] = useState('');
  const [presetSearch, setPresetSearch] = useState('');

  const refs = useMemo(() => {
    const all = getFieldRefs(allFields || [], { includeCalcTableRefs: true });
    return all.filter(r => {
      // Skip self-refs when editing a regular field's formula
      if (currentFieldId && r.insert === `{${currentFieldId}}`) return false;
      if (!refSearch.trim()) return true;
      const q = refSearch.toLowerCase();
      return r.label.toLowerCase().includes(q) || r.insert.toLowerCase().includes(q);
    });
  }, [allFields, refSearch, currentFieldId]);

  // Group refs for display
  const groupedRefs = useMemo(() => {
    const byGroup = {};
    for (const r of refs) {
      (byGroup[r.group] = byGroup[r.group] || []).push(r);
    }
    return byGroup;
  }, [refs]);

  const presets = useMemo(() => {
    const ctxFilter = (p) => {
      // 'row' context only makes sense for calc_table formula columns; still show
      // the 'field' context presets too — they may include Math patterns the user wants.
      if (context === 'field' && p.context === 'row') return false;
      return true;
    };
    const all = FORMULA_PRESETS.filter(ctxFilter);
    if (!presetSearch.trim()) return all;
    const q = presetSearch.toLowerCase();
    return all.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q)
    );
  }, [presetSearch, context]);

  const presetsByCategory = useMemo(() => {
    const byCat = {};
    for (const p of presets) {
      (byCat[p.category || 'Other'] = byCat[p.category || 'Other'] || []).push(p);
    }
    return byCat;
  }, [presets]);

  const insertText = (text) => {
    const next = (formula || '').trim() ? `${formula} ${text}` : text;
    onChange(next);
  };

  const applyPreset = (p) => {
    if ((formula || '').trim() && !window.confirm(`Replace the current formula with "${p.name}"?`)) return;
    onChange(p.expression);
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap pt-1" data-testid={`${testIdPrefix}-helper`}>
      {/* Insert field reference */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1 border-[#0E7490] text-[#0E7490] hover:bg-[#0E7490]/5"
            data-testid={`${testIdPrefix}-insert-ref-btn`}
            title="Insert a reference to another field"
          >
            <Plus className="w-3 h-3" />
            Insert field ref
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[340px] p-0" data-testid={`${testIdPrefix}-ref-picker`}>
          <div className="p-2 border-b border-[#E2E8F0]">
            <Input
              placeholder="Search fields…"
              value={refSearch}
              onChange={e => setRefSearch(e.target.value)}
              className="h-8 text-xs"
              autoFocus
              data-testid={`${testIdPrefix}-ref-search`}
            />
          </div>
          <div className="max-h-[320px] overflow-y-auto">
            {Object.keys(groupedRefs).length === 0 ? (
              <div className="p-4 text-xs text-[#94A3B8] text-center">
                No matching fields. Add more fields in this form to reference them here.
              </div>
            ) : (
              Object.entries(groupedRefs).map(([group, items]) => (
                <div key={group} className="border-b border-[#F1F5F9] last:border-b-0">
                  <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#64748B] bg-[#F8FAFC]">{group}</div>
                  {items.map((r, i) => (
                    <button
                      key={`${group}-${i}`}
                      type="button"
                      onClick={() => insertText(r.insert)}
                      className="w-full text-left px-3 py-1.5 hover:bg-[#F0FDFA] transition-colors flex items-center justify-between gap-2"
                      data-testid={`${testIdPrefix}-ref-option`}
                    >
                      <span className="text-xs text-[#0A2342] truncate">{r.label}</span>
                      <code className="text-[10px] text-[#0E7490] font-mono shrink-0">{r.insert}</code>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
          <div className="p-2 border-t border-[#E2E8F0] bg-[#F8FAFC] text-[10px] text-[#64748B]">
            Clicking a row <strong>appends</strong> the token to your formula.
          </div>
        </PopoverContent>
      </Popover>

      {/* Preset library */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1 border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/5"
            data-testid={`${testIdPrefix}-insert-preset-btn`}
            title="Load a pre-canned formula"
          >
            <Sparkles className="w-3 h-3" />
            Preset formulas
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[380px] p-0" data-testid={`${testIdPrefix}-preset-picker`}>
          <div className="p-2 border-b border-[#E2E8F0]">
            <Input
              placeholder="Search presets (BMI, age, line total…)"
              value={presetSearch}
              onChange={e => setPresetSearch(e.target.value)}
              className="h-8 text-xs"
              autoFocus
              data-testid={`${testIdPrefix}-preset-search`}
            />
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {Object.keys(presetsByCategory).length === 0 ? (
              <div className="p-4 text-xs text-[#94A3B8] text-center">No matching presets.</div>
            ) : (
              Object.entries(presetsByCategory).map(([cat, items]) => (
                <div key={cat} className="border-b border-[#F1F5F9] last:border-b-0">
                  <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#64748B] bg-[#F8FAFC]">{cat}</div>
                  {items.map((p, i) => (
                    <button
                      key={`${cat}-${i}`}
                      type="button"
                      onClick={() => applyPreset(p)}
                      className="w-full text-left px-3 py-2 hover:bg-[#F5F3FF] transition-colors"
                      data-testid={`${testIdPrefix}-preset-option`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-[#0A2342]">{p.name}</span>
                        <span className="text-[10px] text-[#7C3AED]">insert</span>
                      </div>
                      {p.description && <div className="text-[10px] text-[#64748B] mt-0.5">{p.description}</div>}
                      <code className="block text-[10px] text-[#0E7490] font-mono mt-1 truncate">{p.expression}</code>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
          <div className="p-2 border-t border-[#E2E8F0] bg-[#F8FAFC] text-[10px] text-[#64748B]">
            Presets use placeholder names like <code>{'{weight_kg}'}</code>. After inserting, click &quot;Insert field ref&quot; to swap them for your actual field IDs.
          </div>
        </PopoverContent>
      </Popover>

      {/* Cheatsheet */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1 text-[#64748B] hover:bg-[#F1F5F9]"
            data-testid={`${testIdPrefix}-cheatsheet-btn`}
            title="Syntax reference"
          >
            <Info className="w-3 h-3" />
            Syntax
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[360px] text-xs">
          <div className="space-y-2">
            <div className="font-semibold text-[#0A2342]">Supported syntax</div>
            <ul className="space-y-1 text-[#334155]">
              <li>• Operators: <code>+ − × ÷ % **</code> and ternary <code>a&gt;b?x:y</code></li>
              <li>• <code>Math.round/floor/ceil/abs/max/min/sqrt/pow/log/exp</code></li>
              <li>• Field ref: <code>{'{field_id}'}</code></li>
              {context === 'row' && (
                <>
                  <li>• This row&apos;s cell: <code>row.colKey</code></li>
                  <li>• Column aggregate: <code>col.colKey.sum</code> (or avg/count/min/max)</li>
                </>
              )}
              <li>• Another table&apos;s column sum: <code>{'{tableId.col.colKey.sum}'}</code></li>
              <li>• Another table&apos;s specific row: <code>{'{tableId.row.0.colKey}'}</code></li>
              <li>• Age from date: <code>AGE({'{dob}'})</code></li>
              <li>• Days between: <code>DAYS_BETWEEN({'{a}'}, {'{b}'})</code></li>
            </ul>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
